<template>
  <footer class="dynamic-footer" :class="`flavor-${footerContent.flavorKey}`" itemscope itemtype="https://schema.org/Organization">
    <div class="container footer-shell">
      <div class="footer-top-strip">
        <!-- Top tagline and main logo removed for cleaner footer -->
      </div>

      <div class="footer-main-grid">
        <aside class="brand-panel">
          <img :src="footerContent.panelLogoSrc" :alt="footerContent.panelLogoAlt" class="panel-logo">
          <p v-if="brandName" class="brand-name" itemprop="name">{{ brandName }}</p>
          <address v-if="addressTitle || addressLines.length" class="panel-description" itemprop="address">
            <strong v-if="addressTitle">{{ addressTitle }}</strong><br v-if="addressTitle">
            <span v-for="line in addressLines" :key="line">{{ line }}</span>
          </address>
          <ul class="social-list">
            <li v-for="social in footerContent.socialLinks" :key="social.label">
              <a :href="social.href.toLowerCase()" target="_blank" rel="noopener noreferrer" :aria-label="social.label">
                <i :class="social.iconClass"></i>
              </a>
            </li>
          </ul>
        </aside>

        <nav class="footer-nav">
          <ul>
            <li v-for="link in footerContent.columns[0]?.links || []" :key="link.label">
              <a :href="link.href.toLowerCase()">{{ link.label }}</a>
            </li>
          </ul>
        </nav>

        <section v-if="branchList.length" class="footer-branches">
          <h6 class="footer-section-title">Our Branches</h6>
          <ul>
            <li v-for="branch in branchList" :key="branch.id">
              <i class="bi bi-geo-alt-fill"></i>
              <span>{{ branch.branch_name }}</span>
            </li>
          </ul>
        </section>

        <section class="footer-legal">
          <ul>
            <li v-for="link in footerContent.columns[footerContent.columns.length-1]?.links || []" :key="link.label">
              <a :href="link.href.toLowerCase()">{{ link.label }}</a>
            </li>
          </ul>
        </section>

        <section class="footer-contact">
          <ul>
            <li v-for="item in footerContent.contact.items" :key="item.label">
              <i :class="item.iconClass"></i>
              <a v-if="item.href" :href="item.href.toLowerCase()">{{ item.label }}</a>
              <span v-else>{{ item.label }}</span>
            </li>
          </ul>
        </section>
      </div>

      <div class="footer-trust-strip">
        <!-- Trust badges removed for cleaner layout -->
      </div>

      <div class="footer-copyright">
        <p>
          {{ footerContent.copyright.text }}<br>
          <span style="font-size: 13px; color: #f4cd7a;">
            Powered by <a href="https://intendtech.com" target="_blank" rel="noopener" style="color: #f4cd7a; text-decoration: underline;">Intend Technosoft Private Limited</a><br>
            <a href="https://gopgms.com" target="_blank" rel="noopener" style="color: #f4cd7a; text-decoration: underline;">gopgms.com</a> — GoPGMS is a product of Intend Tech, providing online booking services.
          </span>
        </p>
      </div>

      <!-- Apple Touch Icon for SEO and Apple devices -->
      <link rel="apple-touch-icon" :href="footerContent.flavorKey ? `/assets/flavors/${footerContent.flavorKey}/images/apple-touch-icon.png` : '/assets/flavors/default/images/apple-touch-icon.png'" />
    </div>
  </footer>
</template>

<script setup>
import { computed, inject } from 'vue'
import { useAccountStore } from '@/stores/account'
import { resolveFlavor } from '@/config/flavors'
import { resolveFooterContent } from '@/data/footerContent'

const flavor = inject('flavor', resolveFlavor())
const hostname = typeof window !== 'undefined' ? window.location.hostname : ''

const footerContent = computed(() => resolveFooterContent(hostname || flavor?.domains?.[0] || ''))

// Account store provides branch details from the initial account resolution
const accountStore = useAccountStore()

const brandName = computed(() => flavor?.displayName || '')

const primaryBranch = computed(() => accountStore.branches?.[0] ?? null)

const branchList = computed(() => accountStore.branches || [])

const addressTitle = computed(() => primaryBranch.value?.branch_name || '')

const addressLines = computed(() => {
  if (!primaryBranch.value) return []

  const lines = []
  if (primaryBranch.value.address) lines.push(primaryBranch.value.address)

  const cityState = [primaryBranch.value.city, primaryBranch.value.state].filter(Boolean).join(' - ')
  if (cityState) lines.push(cityState)

  return lines
})
</script>

<style scoped>

.dynamic-footer {
  color: #e9e9ea;
  background: radial-gradient(circle at 30% 30%, rgba(255, 208, 120, 0.12), transparent 45%), #0f0d12;
  padding-top: 12px;
  padding-bottom: 12px;
}

.footer-shell {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.footer-top-strip {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 8px;
}

.brand-logo {
  width: clamp(90px, 12vw, 140px);
  object-fit: contain;
}

.top-tagline {
  color: #f4f4f5;
  font-size: clamp(14px, 1.2vw, 22px);
  line-height: 1.2;
}

.footer-main-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-start;
}

.brand-panel {
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  padding-right: 12px;
  min-width: 180px;
}

.panel-logo {
  max-width: 90px;
  margin-bottom: 8px;
}

.brand-name {
  color: #f4f4f5;
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 6px;
}

.footer-section-title {
  color: #f4cd7a;
  font-size: 15px;
  margin: 0 0 8px;
}

.footer-branches ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 6px;
}

.footer-branches ul li {
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.82);
  font-size: 14px;
}

.footer-branches ul li i {
  color: #f4cd7a;
}

.panel-description,
.address-block p,
.app-caption,
.footer-column ul li a,
.footer-column ul li span,
.contact-column ul li a,
.contact-column ul li span {
  color: rgba(255, 255, 255, 0.82);
  font-size: 14px;
}

.address-block {
  margin: 8px 0;
}

.address-block h6,
.footer-column h5,
.contact-column h5,
.payment-block span,
.newsletter label {
  color: #f4cd7a;
  margin-bottom: 4px;
  font-size: 15px;
}

.social-list {
  display: flex;
  gap: 8px;
  margin: 8px 0;
  padding: 0;
  list-style: none;
}

.social-list a {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.09);
  color: #f8d78b;
}

.app-badge {
  display: inline-flex;
  padding: 10px 16px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  color: #fff;
}

.content-panel {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(5, minmax(140px, 1fr));
}

.footer-column ul,
.contact-column ul,
.payment-block ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 8px;
}

.contact-column ul li {
  display: flex;
  align-items: center;
  gap: 10px;
}

.contact-column ul li i {
  color: #f4cd7a;
}

.contact-column ul li.emphasize {
  background: linear-gradient(90deg, #f8dda0, #d9a545);
  border-radius: 8px;
  padding: 8px 12px;
}

.contact-column ul li.emphasize a {
  color: #2b1c07;
  font-weight: 700;
}

.footer-trust-strip {
  padding: 8px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.trust-badges {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.trust-card {
  border: 1px solid rgba(244, 205, 122, 0.45);
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 13px;
}

.payment-block ul {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.payment-block li {
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 999px;
  padding: 4px 8px;
  font-size: 13px;
}

.newsletter-input-wrap {
  display: flex;
  overflow: hidden;
  border-radius: 10px;
}

.newsletter-input-wrap input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  padding: 10px 12px;
}

.newsletter-cta {
  background: linear-gradient(90deg, #f8dda0, #d9a545);
  color: #2b1c07;
  font-weight: 700;
  padding: 10px 16px;
}

.footer-copyright {
  text-align: center;
  padding-bottom: 8px;
  font-size: 13px;
}

@media (max-width: 1199px) {
  .content-panel {
    grid-template-columns: repeat(3, minmax(170px, 1fr));
  }
}

@media (max-width: 991px) {
  .footer-main-grid,
  .footer-trust-strip {
    grid-template-columns: 1fr;
  }

  .brand-panel {
    border-right: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    padding-right: 0;
    padding-bottom: 20px;
  }
}

@media (max-width: 767px) {
  .footer-top-strip {
    flex-direction: column;
    align-items: flex-start;
  }

  .content-panel {
    grid-template-columns: repeat(2, minmax(130px, 1fr));
  }
}

@media (max-width: 575px) {
  .content-panel {
    grid-template-columns: 1fr;
  }
}
</style>
